// src/pages/Dashboard/sections/network-tabs/CommissionAnalysisTab.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createWithdrawalRequest, getCommissionWithdrawalStatus } from '../../../../services/withdrawalService';
import WithdrawalHistoryModal from './WithdrawalHistoryModal';

/**
 * Commission Analysis Tab Component
 * Displays commission history and summary data for network nodes
 * Enhanced with withdrawal functionality
 * OPTIMIZED: Reduced unnecessary data fetches and refreshes
 */
const CommissionAnalysisTab = ({
  commissionData,
  commissionPeriod,
  setCommissionPeriod,
  formatNumber,
  formatWalletAddress,
  formatDate,
  isLoading = false,
  onFetchCommissionData = () => {},
  wallet,
  selectedNode
}) => {
  const [activeDetailView, setActiveDetailView] = useState('all');
  const [withdrawalStatus, setWithdrawalStatus] = useState({});
  const [isCheckingWithdrawal, setIsCheckingWithdrawal] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState('');
  const [withdrawalSuccess, setWithdrawalSuccess] = useState('');
  const [showWithdrawalHistory, setShowWithdrawalHistory] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawalHistoryFilter, setWithdrawalHistoryFilter] = useState('all');
  
  // Track initial render and pending status
  const isInitialRender = useRef(true);
  const isPeriodChangeProcessing = useRef(false);
  const lastPeriodChangeTime = useRef(Date.now());
  
  // Debouncing function for period changes
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Debounced period change handler
  const debouncedPeriodChange = useRef(
    debounce((period) => {
      if (!isPeriodChangeProcessing.current) {
        isPeriodChangeProcessing.current = true;
        
        // Only fetch if enough time has passed since last change
        const now = Date.now();
        if (now - lastPeriodChangeTime.current > 500) {
          console.log("Fetching commission data after period change to:", period);
          onFetchCommissionData(period);
          lastPeriodChangeTime.current = now;
        }
        
        isPeriodChangeProcessing.current = false;
      }
    }, 500)
  ).current;
  
  // OPTIMIZED: Better handling of period changes
  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    setCommissionPeriod(newPeriod);
    // Process the period change in a debounced manner
    debouncedPeriodChange(newPeriod);
  };
  
  // OPTIMIZATION: Only trigger fetch on actual property change, not initial render
  useEffect(() => {
    // Skip initial render - don't fetch data on first mount
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    // We're handling period changes via the debounced handler
    // This effect is now just a backup in case period changes without
    // going through our handler function
  }, [commissionPeriod, onFetchCommissionData]);
  
  // Check withdrawal status when commission data loads
  useEffect(() => {
    // Only check withdrawal status if:
    // 1. We have commission data
    // 2. A node is selected
    // 3. The wallet is connected
    // 4. We're not already checking status
    if (commissionData && selectedNode && wallet.connected && !isCheckingWithdrawal) {
      // Slight delay to prevent too many requests at once
      const timer = setTimeout(() => {
        checkWithdrawalStatus();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [commissionData, selectedNode]);
  
  // Check withdrawal status for commissions
  const checkWithdrawalStatus = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsCheckingWithdrawal(true);
    setWithdrawalError('');
    
    try {
      const status = await getCommissionWithdrawalStatus(wallet, selectedNode.id);
      setWithdrawalStatus(status);
    } catch (err) {
      console.error('Error checking withdrawal status:', err);
      setWithdrawalError('Failed to check withdrawal status');
    } finally {
      setIsCheckingWithdrawal(false);
    }
  };
  
  // Handle withdrawal request
  const handleWithdrawal = async (nodeId, amount) => {
    if (!wallet.connected) {
      setWithdrawalError('Wallet not connected');
      return;
    }
    
    setIsWithdrawing(true);
    setWithdrawalError('');
    setWithdrawalSuccess('');
    
    try {
      const result = await createWithdrawalRequest(wallet, amount, nodeId);
      setWithdrawalSuccess(`Withdrawal request created successfully! ID: ${result.withdrawal_id}`);
      
      // Refresh withdrawal status
      await checkWithdrawalStatus();
      
      // Refresh commission data after a short delay
      setTimeout(() => {
        onFetchCommissionData(commissionPeriod);
      }, 2000);
      
    } catch (err) {
      console.error('Error creating withdrawal request:', err);
      setWithdrawalError(err.message || 'Failed to create withdrawal request');
    } finally {
      setIsWithdrawing(false);
    }
  };
  
  // Toggle withdrawal history modal
  const toggleWithdrawalHistory = () => {
    setShowWithdrawalHistory(!showWithdrawalHistory);
  };
  
  // Get commission details based on active view
  const getFilteredCommissions = () => {
    if (!commissionData?.details?.commissions) return [];
    
    if (activeDetailView === 'all') return commissionData.details.commissions;
    return commissionData.details.commissions.filter(c => c.commission_type === activeDetailView);
  };
  
  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    return formatDate ? 
      formatDate(dateString, true) : // Use formatDate with time if available
      new Date(dateString).toLocaleString(); // Fallback
  };

  // Getting filtered commission data
  const filteredCommissions = getFilteredCommissions();
  
  // Get summary data with proper fallbacks
  const summary = commissionData?.summary || {
    total_commissions: 0,
    referrer_commissions: 0,
    node_commissions: 0,
    differential_commissions: 0,
    commission_count: 0
  };
  
  // Check if commissions can be withdrawn
  const canWithdraw = () => {
    // Check if there's a pending withdrawal first
    if (withdrawalStatus?.pending_withdrawal) {
      return false;
    }
    
    // Check available balance
    return withdrawalStatus?.available_balance > 0;
  };
  
  // Get withdrawal status badge class
  const getWithdrawalStatusClass = (status) => {
    switch (status) {
      case 'withdrawn':
        return 'withdrawn-badge';
      case 'pending':
        return 'pending-badge';
      case 'available':
        return 'available-badge';
      default:
        return '';
    }
  };
  
  return (
    <div className="commission-content">
      <div className="points-controls">
        <h3>Commission Analysis</h3>
        <div className="filters-container">
          <div className="period-selector">
            <label>Time Period:</label>
            <select 
              value={commissionPeriod} 
              onChange={handlePeriodChange} 
              disabled={isLoading}
            >
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
          
          {commissionData?.details?.commissions && (
            <div className="commission-type-filter">
              <label>Commission Type:</label>
              <div className="filter-buttons">
                <button 
                  className={`filter-btn ${activeDetailView === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveDetailView('all')}
                >
                  All Types
                </button>
                <button 
                  className={`filter-btn ${activeDetailView === 'referrer' ? 'active' : ''}`}
                  onClick={() => setActiveDetailView('referrer')}
                >
                  Referrer
                </button>
                <button 
                  className={`filter-btn ${activeDetailView === 'node' ? 'active' : ''}`}
                  onClick={() => setActiveDetailView('node')}
                >
                  Node
                </button>
                <button 
                  className={`filter-btn ${activeDetailView === 'differential' ? 'active' : ''}`}
                  onClick={() => setActiveDetailView('differential')}
                >
                  Differential
                </button>
              </div>
            </div>
          )}
          
          {/* Withdrawal History Button */}
          <div className="withdrawal-actions">
            <button 
              className="btn btn-outline btn-sm"
              onClick={toggleWithdrawalHistory}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              View Withdrawal History
            </button>
          </div>
        </div>
      </div>
      
      {/* Withdrawal Status Section */}
      {!isCheckingWithdrawal && withdrawalStatus && (
        <div className="withdrawal-status-panel">
          <div className="withdrawal-status-content">
            <div className="withdrawal-status-item">
              <div className="status-label">Available Balance:</div>
              <div className="status-value">{formatNumber(withdrawalStatus.available_balance || 0, 2)}</div>
            </div>
            
            <div className="withdrawal-status-item">
              <div className="status-label">Pending Withdrawals:</div>
              <div className="status-value">{formatNumber(withdrawalStatus.pending_amount || 0, 2)}</div>
            </div>
            
            <div className="withdrawal-status-item">
              <div className="status-label">Total Withdrawn:</div>
              <div className="status-value">{formatNumber(withdrawalStatus.total_withdrawn || 0, 2)}</div>
            </div>
            
            <div className="withdrawal-actions">
              <button 
                className="btn btn-primary btn-sm"
                disabled={!canWithdraw() || isWithdrawing}
                onClick={() => handleWithdrawal(selectedNode.id, withdrawalStatus.available_balance)}
              >
                {isWithdrawing ? (
                  <>
                    <span className="spinner-sm"></span>
                    Processing...
                  </>
                ) : (
                  'Withdraw Available Balance'
                )}
              </button>
            </div>
          </div>
          
          {withdrawalError && (
            <div className="withdrawal-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{withdrawalError}</span>
            </div>
          )}
          
          {withdrawalSuccess && (
            <div className="withdrawal-success">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{withdrawalSuccess}</span>
            </div>
          )}
          
          {withdrawalStatus.pending_withdrawal && (
            <div className="pending-withdrawal-alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>
                You have a pending withdrawal request of {formatNumber(withdrawalStatus.pending_amount, 2)} USDT.
                Request ID: {withdrawalStatus.pending_withdrawal_id}
              </span>
            </div>
          )}
        </div>
      )}
      
      {commissionData ? (
        <>
          <div className="commission-summary-cards">
            <div className="commission-card total">
              <div className="commission-value">
                {formatNumber(summary.total_commissions, 2)}
              </div>
              <div className="commission-label">Total Commissions</div>
            </div>
            <div className="commission-card referrer">
              <div className="commission-value">
                {formatNumber(summary.referrer_commissions, 2)}
              </div>
              <div className="commission-label">Referrer Commissions</div>
            </div>
            <div className="commission-card node">
              <div className="commission-value">
                {formatNumber(summary.node_commissions, 2)}
              </div>
              <div className="commission-label">Node Commissions</div>
            </div>
            <div className="commission-card differential">
              <div className="commission-value">
                {formatNumber(summary.differential_commissions, 2)}
              </div>
              <div className="commission-label">Differential Commissions</div>
            </div>
          </div>
          
          {/* Nodes Section */}
          {commissionData.nodes && commissionData.nodes.length > 0 && (
            <div className="commission-nodes-section">
              <h3>Commissions by Node</h3>
              <div className="table-responsive">
                <table className="commission-table">
                  <thead>
                    <tr>
                      <th>Node</th>
                      <th>Tier</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Referrer</th>
                      <th>Node</th>
                      <th>Differential</th>
                      <th>Withdrawal Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionData.nodes.map((node, index) => {
                      // Get the node's withdrawal status
                      const nodeWithdrawalStatus = withdrawalStatus?.node_statuses?.[node.id] || {};
                      const withdrawable = nodeWithdrawalStatus.available_amount > 0;
                      const status = nodeWithdrawalStatus.status || 'unavailable';
                      
                      return (
                        <tr key={node.id || index}>
                          <td className="node-col">{node.reference_code}</td>
                          <td>{node.tier}</td>
                          <td>
                            <span className={`status-badge ${node.status?.toLowerCase() || 'active'}`}>
                              {node.status || 'Active'}
                            </span>
                          </td>
                          <td className="value-col">{formatNumber(node.commissions?.total, 2)}</td>
                          <td className="value-col">{formatNumber(node.commissions?.referrer_commissions, 2)}</td>
                          <td className="value-col">{formatNumber(node.commissions?.node_commissions, 2)}</td>
                          <td className="value-col">{formatNumber(node.commissions?.differential_commissions, 2)}</td>
                          <td>
                            <span className={`withdrawal-status-badge ${getWithdrawalStatusClass(status)}`}>
                              {status === 'withdrawn' ? 'Withdrawn' : 
                               status === 'pending' ? 'Pending Withdrawal' : 
                               status === 'available' ? 'Available' : 'Not Available'}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="withdraw-btn"
                              disabled={!withdrawable || isWithdrawing}
                              onClick={() => handleWithdrawal(node.id, nodeWithdrawalStatus.available_amount)}
                            >
                              {isWithdrawing && node.id === selectedNode.id ? (
                                <span className="spinner-sm"></span>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 5v14M7 10l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                              Withdraw
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Commission Sources */}
          {commissionData.commission_sources && (
            <div className="commission-sources-section">
              <h3>Commission Sources</h3>
              
              {/* Direct Nodes */}
              {commissionData.commission_sources.direct_nodes && 
               commissionData.commission_sources.direct_nodes.length > 0 && (
                <div className="commission-source-group">
                  <h4>Direct Subordinate Nodes</h4>
                  <div className="table-responsive">
                    <table className="commission-table">
                      <thead>
                        <tr>
                          <th>Node</th>
                          <th>Wallet</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Commissions Generated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionData.commission_sources.direct_nodes.map((node, index) => (
                          <tr key={node.id || index}>
                            <td className="node-col">{node.reference_code}</td>
                            <td className="wallet-col">{formatWalletAddress(node.wallet_address)}</td>
                            <td>{node.tier}</td>
                            <td>
                              <span className={`status-badge ${node.status?.toLowerCase() || 'active'}`}>
                                {node.status || 'Active'}
                              </span>
                            </td>
                            <td className="value-col">{formatNumber(node.commissions_generated?.total, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Second Level Nodes */}
              {commissionData.commission_sources.second_level_nodes && 
               commissionData.commission_sources.second_level_nodes.length > 0 && (
                <div className="commission-source-group">
                  <h4>Second Level Nodes</h4>
                  <div className="table-responsive">
                    <table className="commission-table">
                      <thead>
                        <tr>
                          <th>Node</th>
                          <th>Wallet</th>
                          <th>Tier</th>
                          <th>Status</th>
                          <th>Commissions Generated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionData.commission_sources.second_level_nodes.map((node, index) => (
                          <tr key={node.id || index}>
                            <td className="node-col">{node.reference_code}</td>
                            <td className="wallet-col">{formatWalletAddress(node.wallet_address)}</td>
                            <td>{node.tier}</td>
                            <td>
                              <span className={`status-badge ${node.status?.toLowerCase() || 'active'}`}>
                                {node.status || 'Active'}
                              </span>
                            </td>
                            <td className="value-col">{formatNumber(node.commissions_generated?.total, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Detailed Commission Records */}
          {commissionData.details && commissionData.details.commissions && commissionData.details.commissions.length > 0 && (
            <div className="commission-details-section">
              <h3>Commission Transaction History</h3>
              <div className="table-responsive">
                <table className="commission-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Reference</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Rate</th>
                      <th>Status</th>
                      <th>Order ID</th>
                      <th>Source Node</th>
                      <th>Withdrawal Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCommissions.map((commission, index) => {
                      // Get commission withdrawal status from the status data
                      const commWithdrawalStatus = 
                        withdrawalStatus?.commission_statuses?.[commission.id] || 
                        { status: 'unavailable' };
                      
                      return (
                        <tr key={commission.id || index} 
                            className={`commission-type-${commission.commission_type}`}>
                          <td className="date-col">{formatDateTime(commission.created_at)}</td>
                          <td className="reference-col">{commission.reference_code}</td>
                          <td>
                            <span className={`commission-type-badge ${commission.commission_type}`}>
                              {commission.commission_type?.charAt(0).toUpperCase() + commission.commission_type?.slice(1)}
                            </span>
                          </td>
                          <td className="value-col">{formatNumber(commission.amount, 2)}</td>
                          <td>{commission.rate_percentage}%</td>
                          <td>
                            <span className={`status-badge ${commission.status?.toLowerCase() || 'completed'}`}>
                              {commission.status || 'Completed'}
                            </span>
                          </td>
                          <td className="order-col">{commission.order_id || 'N/A'}</td>
                          <td>
                            {commission.source_node ? 
                              commission.source_node.reference_code : 
                              'N/A'}
                          </td>
                          <td>
                            <span className={`withdrawal-status-badge ${getWithdrawalStatusClass(commWithdrawalStatus.status)}`}>
                              {commWithdrawalStatus.status === 'withdrawn' ? 'Withdrawn' : 
                               commWithdrawalStatus.status === 'pending' ? 'Pending' : 
                               commWithdrawalStatus.status === 'available' ? 'Available' : 'Not Available'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {commissionData.details.pagination && (
                <div className="pagination">
                  <button 
                    className="pagination-btn"
                    onClick={() => onFetchCommissionData(commissionPeriod, 1)}
                    disabled={!commissionData.details.pagination.has_previous}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => onFetchCommissionData(commissionPeriod, commissionData.details.pagination.current_page - 1)}
                    disabled={!commissionData.details.pagination.has_previous}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  <span className="pagination-info">
                    Page {commissionData.details.pagination.current_page} of {commissionData.details.pagination.total_pages}
                  </span>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => onFetchCommissionData(commissionPeriod, commissionData.details.pagination.current_page + 1)}
                    disabled={!commissionData.details.pagination.has_next}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => onFetchCommissionData(commissionPeriod, commissionData.details.pagination.total_pages)}
                    disabled={!commissionData.details.pagination.has_next}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="no-data-message">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>No commission data available for the selected period.</p>
        </div>
      )}
      
      {/* Withdrawal History Modal */}
      {showWithdrawalHistory && (
        <WithdrawalHistoryModal 
          isOpen={showWithdrawalHistory}
          onClose={toggleWithdrawalHistory}
          wallet={wallet}
          formatNumber={formatNumber}
          formatDate={formatDateTime}
        />
      )}
    </div>
  );
};

export default CommissionAnalysisTab;
